import React from 'react';
import { Tabs, Tab, Box, styled, useTheme, useMediaQuery } from '@mui/material';
import { Outlet, Link, useLocation, matchPath } from 'react-router-dom';
import { Person as ProfileIcon, Settings as ServicesIcon } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

// Animation variants
const tabContentVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.2,
      ease: 'easeIn'
    }
  }
};

// Styled Tab with responsive design
const LinkTab = styled((props: any) => <Tab component={Link} iconPosition="start" {...props} />)(
  ({ theme }) => ({
    textTransform: 'none',
    fontWeight: 500,
    minHeight: '48px',
    padding: theme.spacing(1, 1.5),
    color: theme.palette.text.secondary,
    position: 'relative',
    overflow: 'hidden',
    '& .MuiTab-iconWrapper': {
      marginRight: theme.spacing(0.5),
      fontSize: '1.1rem'
    },
    '&.Mui-selected': {
      color: theme.palette.primary.main,
      fontWeight: 600
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1.5, 2),
      '& .MuiTab-iconWrapper': {
        marginRight: theme.spacing(1),
        fontSize: '1.2rem'
      }
    }
  })
);

// Tab Configuration with icons
const tabConfig = [
  {
    label: 'Profile',
    path: '/profile-settings',
    exact: true,
    icon: <ProfileIcon fontSize="inherit" />
  },
  {
    label: 'Services',
    path: '/profile-settings/services',
    exact: false,
    icon: <ServicesIcon fontSize="inherit" />
  }
];

const ProfileSettings = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Match active tab using matchPath
  const activeTab = tabConfig.findIndex((tab) => {
    const pattern = tab.exact ? tab.path : `${tab.path}/*`;
    return matchPath({ path: pattern, end: tab.exact }, currentPath);
  });

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'hidden'
      }}
    >
      {/* Animated Tabs */}
      <Box
        component={motion.div}
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar - 1,
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[1]
        }}
      >
        <Tabs
          value={activeTab === -1 ? 0 : activeTab}
          variant="fullWidth"
          textColor="primary"
          sx={{
            '& .MuiTabs-indicator': {
              display: 'none' // Use custom indicator
            }
          }}
        >
          {tabConfig.map((tab, index) => (
            <LinkTab
              key={index}
              label={isSmallScreen ? '' : tab.label}
              icon={tab.icon}
              to={tab.path}
              sx={{
                minWidth: isSmallScreen ? 'auto' : undefined,
                // px: isSmallScreen ? 1 : undefined
              }}
            />
          ))}
        </Tabs>

        {/* Custom animated indicator */}
        <Box sx={{ position: 'relative', height: 3 }}>
          <motion.div
            style={{
              position: 'absolute',
              bottom: 0,
              left: `${(activeTab === -1 ? 0 : activeTab) * (100 / tabConfig.length)}%`,
              width: `${100 / tabConfig.length}%`,
              height: '100%',
              backgroundColor: theme.palette.primary.main,
              borderRadius: 2
            }}
            animate={{
              x: 0
            }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 20
            }}
          />
        </Box>
      </Box>

      {/* Animated Content Area */}
      <Box
        // sx={{
        // //   p: isSmallScreen ? 1 : 2,
        //   [theme.breakpoints.up('md')]: {
        //     p: 3
        //   }
        // }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={tabContentVariants}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default ProfileSettings;
